import { useEffect, useMemo, useRef, useState } from "react";

import type { PrinterCapabilities, PrintOptions } from "../api/printers";

/**
 * KraetdNet's own print dialog: a preview of the file plus only the settings this printer
 * really supports (from its driver), so nothing is offered that the printer can't do. Shown
 * after a file is picked; nothing prints until "Print" is clicked.
 */
export default function PrintDialog({
  printerName,
  file,
  capabilities,
  onPrint,
  onCancel,
}: {
  printerName: string;
  file: File;
  capabilities: PrinterCapabilities | null | undefined;
  onPrint: (copies: number, options: PrintOptions) => void;
  onCancel: () => void;
}) {
  const caps = capabilities && capabilities.sizes ? capabilities : null;
  const [copiesInput, setCopiesInput] = useState("1");
  // "" = the printer's own setting (nothing is sent unless the person picks something).
  const [sides, setSides] = useState<"" | NonNullable<PrintOptions["sides"]>>("");
  const [color, setColor] = useState<"" | NonNullable<PrintOptions["color"]>>("");
  const [paper, setPaper] = useState("");
  const [tray, setTray] = useState("");
  const firstField = useRef<HTMLInputElement>(null);

  const kind = useMemo(() => previewKind(file), [file]);
  const url = useMemo(() => (kind === "none" ? "" : URL.createObjectURL(file)), [file, kind]);
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);

  // Focus once, when the dialog opens (the page refreshes its data in the background; that
  // must not pull focus away from whatever the person is choosing).
  useEffect(() => {
    firstField.current?.focus();
  }, []);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function submit() {
    const n = parseInt(copiesInput, 10);
    const copies = Number.isFinite(n) ? Math.min(Math.max(n, 1), 99) : 1;
    const options: PrintOptions = {};
    if (sides) options.sides = sides;
    if (color) options.color = color;
    if (paper) options.paper = paper;
    if (tray) options.tray = tray;
    onPrint(copies, options);
  }

  const trays = (caps?.bins ?? []).filter((b) => !/auto/i.test(b));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="print-dialog-title"
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex min-h-[240px] flex-1 items-center justify-center bg-slate-100 md:min-h-[480px]">
          {kind === "pdf" ? (
            <iframe src={url} title={`Preview of ${file.name}`} className="h-full min-h-[240px] w-full md:min-h-[480px]" />
          ) : kind === "image" ? (
            <img src={url} alt={`Preview of ${file.name}`} className="max-h-[70vh] max-w-full object-contain p-4" />
          ) : (
            <p className="p-6 text-center text-sm text-slate-600">
              No preview for this kind of file.
              <br />
              It prints the way it looks in its own program.
            </p>
          )}
        </div>

        <form
          className="flex w-full flex-col gap-4 p-6 md:w-80"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div>
            <h2 id="print-dialog-title" className="text-lg font-semibold text-slate-900">
              Print
            </h2>
            <p className="mt-1 truncate text-sm text-slate-600" title={file.name}>
              {file.name}
            </p>
            <p className="text-sm text-slate-600">on {printerName}</p>
          </div>

          <label className="flex flex-col text-sm text-slate-700">
            <span className="mb-1 font-medium">Copies</span>
            <input
              ref={firstField}
              type="number"
              min={1}
              max={99}
              value={copiesInput}
              onChange={(e) => setCopiesInput(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2"
            />
          </label>

          {caps?.duplex ? (
            <label className="flex flex-col text-sm text-slate-700">
              <span className="mb-1 font-medium">Sides</span>
              <select
                value={sides}
                onChange={(e) => setSides(e.target.value as typeof sides)}
                className="rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="">Printer&rsquo;s setting</option>
                <option value="one-sided">One side</option>
                <option value="two-sided-long-edge">Both sides (flip on long edge)</option>
                <option value="two-sided-short-edge">Both sides (flip on short edge)</option>
              </select>
            </label>
          ) : null}

          {!caps || caps.color ? (
            <label className="flex flex-col text-sm text-slate-700">
              <span className="mb-1 font-medium">Colors</span>
              <select
                value={color}
                onChange={(e) => setColor(e.target.value as typeof color)}
                className="rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="">Printer&rsquo;s setting</option>
                <option value="color">Color</option>
                <option value="monochrome">Black and white</option>
              </select>
            </label>
          ) : null}

          {caps && caps.sizes.length > 0 ? (
            <label className="flex flex-col text-sm text-slate-700">
              <span className="mb-1 font-medium">Paper size</span>
              <select
                value={paper}
                onChange={(e) => setPaper(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="">Same as the document</option>
                {caps.sizes.map((s) => (
                  <option key={`${s.name}-${s.w}-${s.h}`} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {trays.length > 0 ? (
            <label className="flex flex-col text-sm text-slate-700">
              <span className="mb-1 font-medium">Paper tray</span>
              <select
                value={tray}
                onChange={(e) => setTray(e.target.value)}
                className="rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="">Automatic</option>
                {trays.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div className="mt-auto flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="no-tap-highlight rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="no-tap-highlight rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Print
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function previewKind(file: File): "pdf" | "image" | "none" {
  const name = file.name.toLowerCase();
  if (file.type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (/\.(png|jpe?g|gif|bmp)$/.test(name) || /^image\/(png|jpeg|gif|bmp)$/.test(file.type)) return "image";
  return "none";
}
